<section
  class="pa"
  role="dialog"
  [attr.aria-label]="'ASSISTANT.TITLE' | translate"
>
  <header class="pa__bar">
    <button
      type="button"
      class="pa__icon-btn"
      [attr.aria-expanded]="historyOpen"
      [attr.aria-label]="'ASSISTANT.HISTORY' | translate"
      [title]="'ASSISTANT.HISTORY' | translate"
      (click)="toggleHistory()"
    >
      <svg cIcon name="cilMenu"></svg>
    </button>
    <h2 class="pa__title">{{ title || ("ASSISTANT.NEW_CHAT" | translate) }}</h2>
    @if (whatsappAllowed) {
    <button
      type="button"
      class="pa__icon-btn"
      [attr.aria-label]="'ASSISTANT.WHATSAPP' | translate"
      [title]="'ASSISTANT.WHATSAPP' | translate"
      (click)="toggleWhatsapp()"
    >
      WA
    </button>
    }
    <button
      type="button"
      class="pa__icon-btn"
      [attr.aria-label]="'ASSISTANT.NEW_CHAT' | translate"
      [title]="'ASSISTANT.NEW_CHAT' | translate"
      (click)="newChat()"
    >
      <svg cIcon name="cilPlus"></svg>
    </button>
    <button
      type="button"
      class="pa__icon-btn"
      [attr.aria-label]="'ASSISTANT.CLOSE' | translate"
      [title]="'ASSISTANT.CLOSE' | translate"
      (click)="close()"
    >
      <svg cIcon name="cilX"></svg>
    </button>
  </header>

  @if (whatsappOpen) {
  <div class="pa__whatsapp">
    <strong>{{ "ASSISTANT.WHATSAPP" | translate }}</strong>
    @for (link of whatsappLinks; track link) {
    <div>
      <p>
        {{ "ASSISTANT.WHATSAPP_LINKED" | translate }}: +{{ link.phoneNumber }}
      </p>
      <button
        type="button"
        class="pa__whatsapp-action"
        [disabled]="whatsappBusy"
        [attr.aria-label]="
          ('ASSISTANT.WHATSAPP_UNLINK' | translate) + ' +' + link.phoneNumber
        "
        (click)="removeWhatsappLink(link.id)"
      >
        {{ "ASSISTANT.WHATSAPP_UNLINK" | translate }}
      </button>
    </div>
    }
    <p>{{ "ASSISTANT.WHATSAPP_HELP" | translate }}</p>
    @if (whatsappNumber) {
    <p>{{ "ASSISTANT.WHATSAPP_NUMBER" | translate }}: +{{ whatsappNumber }}</p>
    } @if (whatsappCode) {
    <p>
      <code>{{ whatsappCode }}</code>
    </p>
    }
    <div class="pa__whatsapp-actions">
      <button
        type="button"
        class="pa__whatsapp-action pa__whatsapp-action--primary"
        [disabled]="whatsappBusy"
        (click)="createWhatsappLink()"
      >
        {{ "ASSISTANT.WHATSAPP_CODE" | translate }}
      </button>
      <button
        type="button"
        class="pa__whatsapp-action"
        [disabled]="whatsappBusy"
        (click)="refreshWhatsapp()"
      >
        {{ "ASSISTANT.WHATSAPP_REFRESH" | translate }}
      </button>
    </div>
    @if (whatsappError) {
    <p role="alert">{{ "ASSISTANT.WHATSAPP_ERROR" | translate }}</p>
    }
  </div>
  }

  <div class="pa__body">
    @if (historyOpen) {
    <nav
      class="pa__history"
      [attr.aria-label]="'ASSISTANT.HISTORY' | translate"
    >
      @if (!threads.length) {
      <p class="pa__muted">{{ "ASSISTANT.NO_CHATS" | translate }}</p>
      }
      <ul class="pa__threads">
        @for (thread of threads; track thread) {
        <li [class.is-current]="thread.id === threadId">
          @if (editingId !== thread.id) {
          <button
            type="button"
            class="pa__thread"
            (click)="openThread(thread.id)"
          >
            {{ thread.title || ("ASSISTANT.NEW_CHAT" | translate) }}
          </button>
          <button
            type="button"
            class="pa__icon-btn pa__icon-btn--small"
            [attr.aria-label]="'ASSISTANT.RENAME' | translate"
            [title]="'ASSISTANT.RENAME' | translate"
            (click)="startRename(thread)"
          >
            <svg cIcon name="cilPencil"></svg>
          </button>
          <button
            type="button"
            class="pa__icon-btn pa__icon-btn--small"
            [attr.aria-label]="'ASSISTANT.DELETE' | translate"
            [title]="'ASSISTANT.DELETE' | translate"
            (click)="deleteThread(thread)"
          >
            <svg cIcon name="cilTrash"></svg>
          </button>
          } @else {
          <form class="pa__rename" (ngSubmit)="saveRename(thread)">
            <input
              name="title"
              class="pa__field"
              maxlength="60"
              [(ngModel)]="editTitle"
              [attr.aria-label]="'ASSISTANT.RENAME' | translate"
              (keydown.escape)="editingId = null; $event.stopPropagation()"
            />
            <button
              type="submit"
              class="pa__icon-btn pa__icon-btn--small"
              [attr.aria-label]="'ASSISTANT.SAVE' | translate"
              [title]="'ASSISTANT.SAVE' | translate"
            >
              <svg cIcon name="cilCheck"></svg>
            </button>
          </form>
          }
        </li>
        }
      </ul>
    </nav>
    }

    <!-- column-reverse keeps the newest message in view without scroll code -->
    <div class="pa__scroll">
      <div class="pa__log" aria-live="polite">
        @if (!messages.length && !sending) {
        <p class="pa__muted">{{ "ASSISTANT.EMPTY" | translate }}</p>
        } @for (message of messages; track message) { @if (message.role ===
        'user') {
        <p class="pa__ask">{{ message.content }}</p>
        }
        <!-- V181: Angular's [innerHTML] sanitizer is the only guard — never bypassSecurityTrust* here -->
        @if (message.role === 'assistant') {
        <div class="pa__answer" [innerHTML]="answerHtml(message.content)"></div>
        } } @if (sending) {
        <p class="pa__muted">{{ "ASSISTANT.THINKING" | translate }}</p>
        }
      </div>
    </div>
  </div>

  @if (expired) {
  <div class="pa__expired" role="alert" data-test="assistant-expired">
    <p>{{ "ASSISTANT.SESSION_EXPIRED" | translate }}</p>
    <button
      type="button"
      class="pa__primary"
      data-test="assistant-relogin"
      (click)="relogin()"
    >
      {{ "ASSISTANT.LOG_IN_AGAIN" | translate }}
    </button>
  </div>
  } @else { @if (error) {
  <p class="pa__error" role="alert" data-test="assistant-error">
    {{ error | translate : errorParams }}
  </p>
  }
  <form class="pa__composer" (ngSubmit)="send()">
    <textarea
      name="draft"
      rows="2"
      maxlength="4000"
      class="pa__field"
      data-test="assistant-input"
      [(ngModel)]="draft"
      [placeholder]="'ASSISTANT.PLACEHOLDER' | translate"
      [attr.aria-label]="'ASSISTANT.PLACEHOLDER' | translate"
      (keydown.enter)="onEnter($event)"
    ></textarea>
    <button
      type="submit"
      class="pa__send"
      [disabled]="sending || !draft.trim()"
      [attr.aria-label]="'ASSISTANT.SEND' | translate"
      [title]="'ASSISTANT.SEND' | translate"
    >
      <svg cIcon name="cilArrowTop"></svg>
    </button>
  </form>
  }
</section>
